import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "react-toastify/dist/ReactToastify.css";
import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";
import DiscardDialog from "cxfcomponents/DiscardDialog";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { credentialManagementData } from "../../schemas";
import { encode, decode } from "api/UtilityServiceApis";
import { insertCredential, updateCredential } from "api/CredentialManagementApi";
import {
  closeCredentialDialog,
  setDeleteCredentialDialogOpen,
  setCredentials,
  setSelectedCredential
} from "redux/actions/portal/credentialManagementActionCreator";
import Loading from "components/Loading";
import { CircularProgress } from "@mui/material";
import { useUserRole } from "hooks/useUserRole";
import { truncateInputValue } from "helpers/fieldListeners";
import { FormObserver } from "components/Form/Observer";
import { uploadFile } from "../../../../../helpers/common";
import defaultCredentialIcon from "assets/images/credential.svg";
import CxfFormField from "cxfcomponents/FormField";
import { credentialFormValidation } from "./validation";
import { credentialFormInitialValues } from "./initialValues";
import { EditPictureMenu } from "components/Picture/Edit";
import DeletePropertiesSection from "cxfcomponents/DeletePropertiesSection";
import { useCredentialPermissions } from "hooks/useCredentialPermissions";

export default function CredentialDialogForm({ onClose = Function(), optionApplicationTypes }) {
  const { user } = useAuth0();
  const { credentialDialogMode, selectedCredential, isCredentialDialogOpen, credentials } =
    useSelector((store) => store?.portal?.credentialManagement);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const formRef = useRef(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [validationSchema, setValidationSchema] = useState(credentialFormValidation);
  const [areOtherFieldsRendered, setAreOtherFieldsRendered] = useState(false);
  const dispatch = useDispatch();
  const optionApplicationType =
    optionApplicationTypes?.length === 1
      ? optionApplicationTypes[0]
      : selectedCredential
      ? selectedCredential?.application?.applicationType
      : null;
  const [formInitialValues, setFormInitialValues] = useState({
    ...credentialFormInitialValues,
    applicationType: optionApplicationType ?? ""
  });
  const [isPasswordDecoding, setIsPasswordDecoding] = useState(false);
  const isDisabled = credentialDialogMode === "view";
  const { canDelete } = useCredentialPermissions(selectedCredential);
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const { isOwnerRole, isAdminRole } = useUserRole();
  const isCredentialOwner = selectedCredential?.owner_id === user?.cxfUserId;
  const canViewToken = isCredentialOwner || isOwnerRole || isAdminRole;
  const applicationTypeRef = useRef(null);
  const [pictureUpload, setPictureUpload] = useState(null);

  const credentialApplicationTypeOptions = [
    ...credentialManagementData
      .filter((cred) => {
        if (optionApplicationType) {
          return cred.type === optionApplicationType;
        } else if (optionApplicationTypes?.length > 1) {
          return optionApplicationTypes.includes(cred.type);
        } else {
          return cred;
        }
      })
      .sort((a, b) => a.type.localeCompare(b.type))
      .map((data) => ({
        value: data.type,
        label: data.name
      }))
  ];

  const handleAccept = () => {
    formRef.current.submitForm();
  };

  const addCredential = async (credentialFormData) => {
    const { logo, ...restCredentialFormData } = credentialFormData;
    try {
      let newCredential = {
        application: restCredentialFormData,
        shared_credentials: false,
        role: "owner",
        applicable_components: [restCredentialFormData?.applicationType],
        share_user: [],
        expiration_date: null,
        status: "active",
        owner_id: user?.cxfUserId,
        logo: logo ?? null
      };

      const input = {
        tenantId: user?.stripeId,
        credentials: newCredential
      };

      const insertCredentialResponse = await insertCredential(input);

      if (insertCredentialResponse?.isSuccessful) {
        toast.success(insertCredentialResponse.message);
        newCredential = Object.assign(newCredential, insertCredentialResponse.resultObject);
        newCredential.tenant_id = user?.stripeId;
        newCredential.create_date_time = new Date().toISOString();
        newCredential.updated_date_time = new Date().toISOString();
        dispatch(setCredentials([...credentials, newCredential] || []));
        onClose(insertCredentialResponse?.resultObject);
      } else {
        toast.error(insertCredentialResponse.message);
      }
    } catch (error) {
      console.error("add credential error: ", error);
    }
  };

  const editCredential = async (credentialFormData) => {
    const { logo, ...restCredentialFormData } = credentialFormData;
    try {
      const updatedCredential = {
        application: restCredentialFormData,
        shared_credentials: selectedCredential?.shared_credentials,
        role: selectedCredential?.role,
        applicable_components: [selectedCredential?.application?.applicationType],
        share_user: selectedCredential?.share_user,
        expiration_date: selectedCredential?.expiration_date,
        status: selectedCredential?.status,
        owner_id: selectedCredential?.owner_id,
        updated_date_time: new Date().toISOString(),
        logo
      };

      const input = {
        tenantId: user?.stripeId,
        credentialId: selectedCredential?.id,
        credential: updatedCredential
      };

      const updateCredentialResponse = await updateCredential(input);

      if (!updateCredentialResponse?.isSuccessful) {
        return toast.error(updateCredentialResponse.message);
      }

      toast.success("Successfully updated credential: " + credentialFormData?.name, {});

      const updatedCredentials = credentials.map((credential) => {
        if (credential.id === selectedCredential?.id) {
          return {
            ...credential,
            ...updatedCredential,
            id: credential.id,
            tenant_id: credential.tenant_id,
            create_date_time: credential.create_date_time
          };
        }
        return credential;
      });
      dispatch(setCredentials(updatedCredentials || []));

      onClose();
    } catch (error) {
      console.error("update credential error: ", error);
    }
  };

  const handleSubmitForm = async (values) => {
    if (isFormSubmitting) return;
    setIsFormSubmitting(true);
    const credentialFormData = {};

    try {
      if (values) {
        for (const key of Object.keys(values)) {
          if (key === "password") {
            const encryptedPassword = await encode(values[key]);
            credentialFormData[key] = encryptedPassword;
          } else {
            credentialFormData[key] = values[key];
          }
        }
      }

      if (pictureUpload) {
        const result = await uploadFile(
          user,
          pictureUpload,
          "credentials",
          credentialDialogMode === "add" ? credentialFormData.name : selectedCredential?.id,
          true
        );
        credentialFormData.logo = result?.Location;
      } else if (pictureUpload === "") {
        credentialFormData.logo = "";
      }

      if (credentialDialogMode === "add") {
        await addCredential(credentialFormData);
      }

      if (credentialDialogMode === "edit") {
        await editCredential(credentialFormData);
      }

      formRef.current.resetForm({ values: credentialFormData });
      setIsFormSubmitting(false);
      closeCredentialPopup();
    } catch (error) {
      setIsFormSubmitting(false);
      console.error("handle submit form error: ", error);
    }
  };

  const handleDiscardChanges = () => {
    handleDiscardDialogClose();
    closeCredentialPopup();
  };

  const handleDialogClose = () => {
    setIsTokenVisible(false);
    closeCredentialPopup();
    onClose();
    setTimeout(() => {
      setPictureUpload(null);
    }, 500);
  };

  const closeCredentialPopup = () => {
    dispatch(closeCredentialDialog());
    setTimeout(() => {
      dispatch(setSelectedCredential({}));
      setPictureUpload(null);
    }, 500);
    setFormInitialValues(credentialFormInitialValues);
  };

  const handleFieldChange = (e, formField, setFieldValue) => {
    const { value } = e.target;
    const { field } = formField;

    setIsFormSubmitting(false);

    if (field === "portNumber") {
      const onlyNums = value.replace(/\D/g, "");
      setFieldValue(field, onlyNums);
    } else {
      setFieldValue(field, value);
    }
  };

  const handleDiscardDialogClose = () => {
    setDiscardDialogOpen(false);
  };

  const renderDialogTitle = () => {
    switch (credentialDialogMode) {
      case "add":
        return "Add Credential";
      case "edit":
        return "Edit Credential";
      case "view":
        return "View Credential";
      default:
        return "Credential";
    }
  };

  const getInitialValues = (applicationType, selectedCredential) => {
    const selectedApplicationTypeData = credentialManagementData.find(
      (data) => data.type === applicationType
    );

    const initialValues = selectedCredential?.application
      ? { ...selectedCredential.application }
      : { applicationType: applicationType ?? "", name: "" };

    if (selectedApplicationTypeData) {
      selectedApplicationTypeData.fields.forEach((field) => {
        if (!initialValues[field.field]) {
          initialValues[field.field] = "";
        }
      });
    }

    return initialValues;
  };

  const validateName = (value) => {
    let error;
    const trimmedValue = value ? value.trim() : "";

    if (!trimmedValue) {
      error = "Name is required.";
    }

    if (
      trimmedValue &&
      credentials?.some(
        (credential) =>
          credential?.application?.name?.toLowerCase().trim() === trimmedValue.toLowerCase() &&
          credential?.id !== selectedCredential?.id
      )
    ) {
      error = `${trimmedValue} already exists.`;
    }

    return error;
  };

  const handleUpdateDynamicSchema = (resetForm) => (values) => {
    const applicationType = credentialManagementData.find(
      (data) => data.type === values.applicationType || data.type === optionApplicationType
    );

    const dynamicSchema = {};

    dynamicSchema["name"] = Yup.string().required("Name is required");
    dynamicSchema["applicationType"] = Yup.string().required("applicationType is required");

    if (
      applicationType &&
      (!applicationTypeRef.current || applicationTypeRef.current !== applicationType.type)
    ) {
      setAreOtherFieldsRendered(true);
      applicationType.fields.forEach((field) => {
        if (field.isRequired && field.field !== "name") {
          dynamicSchema[field.field] = Yup.string().required(`${field.label} is required!`);
        }
      });

      setValidationSchema(Yup.object().shape(dynamicSchema));

      const newInitialValues = getInitialValues(values.applicationType, selectedCredential);

      resetForm({
        values: {
          ...newInitialValues,
          name: values.name ?? "",
          description: values.description ?? ""
        }
      });
    } else {
      setAreOtherFieldsRendered(false);
    }

    if (applicationType) {
      applicationTypeRef.current = applicationType.type;
    }
  };

  useEffect(() => {
    setFormInitialValues(getInitialValues(optionApplicationType, selectedCredential));
  }, [optionApplicationType, selectedCredential]);

  useEffect(() => {
    if (!selectedCredential?.id) return;

    const handleDecodePassword = async () => {
      if (!selectedCredential.application?.password) return;

      setIsPasswordDecoding(true);
      const decodedPassword = await decode(selectedCredential.application.password);
      setIsPasswordDecoding(false);
      setFormInitialValues((prev) => ({
        ...prev,
        password: decodedPassword
      }));
    };

    handleDecodePassword();
  }, [selectedCredential?.id]);

  return (
    <>
      <Dialog
        onClose={handleDialogClose}
        fullWidth={true}
        maxWidth="csm"
        open={isCredentialDialogOpen}
        aria-labelledby="credential-dialog-title"
        aria-describedby="credential-dialog-description"
        className="credential-dialog"
        closeAfterTransition={false}
        PaperProps={{ style: { overflowY: areOtherFieldsRendered ? "auto" : "unset" } }}
      >
        <DialogTitle
          id="credential-dialog-title"
          sx={{
            marginBottom: "20px",
            padding: "32px 0 0 48px"
          }}
          className="white-to-dark"
        >
          {renderDialogTitle()}
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "0 48px 24px"
          }}
        >
          <Formik
            enableReinitialize
            initialValues={formInitialValues}
            onSubmit={handleSubmitForm}
            innerRef={formRef}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, errors, touched, resetForm }) => {
              return (
                <Form autoComplete="off">
                  <FormObserver onChange={handleUpdateDynamicSchema(resetForm)} />
                  <SoftBox
                    className="border-top"
                    sx={{
                      alignItems: "flex-start",
                      display: "flex",
                      paddingTop: "20px"
                    }}
                  >
                    <EditPictureMenu
                      pictureUpload={pictureUpload}
                      setPictureUpload={setPictureUpload}
                      value={selectedCredential?.logo}
                      params={selectedCredential?.updated_date_time ?? ""}
                      defaultPicture={defaultCredentialIcon}
                      isDisabled={credentialDialogMode === "view"}
                    />
                    <SoftBox
                      sx={{
                        flexGrow: 1,
                        transform: "translateY(-12px)"
                      }}
                    >
                      <CxfFormField
                        type="text"
                        label="name"
                        name="name"
                        required={!isDisabled}
                        disabled={isDisabled}
                        error={errors["name"]}
                        validate={validateName}
                        placeholder="eg. New Credential"
                        className="no-warning-icon"
                      />
                      <CxfFormField
                        label="description"
                        name="description"
                        type="text"
                        onInput={truncateInputValue(500)}
                        error={errors["description"]}
                        placeholder={"description"}
                        disabled={isDisabled}
                        isMultiline
                        rows={2}
                      />
                    </SoftBox>
                  </SoftBox>
                  <CxfFormField
                    type="select"
                    label="Application type"
                    name="applicationType"
                    defaultValue={
                      values?.applicationType
                        ? credentialApplicationTypeOptions.find(
                            (option) => option.value === values?.applicationType
                          )
                        : { value: "", label: "Select application type" }
                    }
                    required={!isDisabled}
                    options={credentialApplicationTypeOptions}
                    error={errors["applicationType"]}
                    isDisabled={isDisabled || credentialDialogMode === "edit"}
                    menuPlacement="top"
                  />
                  {isPasswordDecoding ? (
                    <SoftBox maxWidth="max-content" mx="auto">
                      <Loading />
                    </SoftBox>
                  ) : (
                    <>
                      {values?.applicationType &&
                        credentialManagementData
                          .find((data) => data.type === values?.applicationType)
                          .fields.map((field) => {
                            if (field.field === "description" && credentialDialogMode === "view") {
                              return null;
                            }

                            return (
                              <CxfFormField
                                key={field.field}
                                label={field.label}
                                name={field.field}
                                type={
                                  field.type === "password" && isTokenVisible ? "text" : field.type
                                }
                                value={values[field.field] ?? ""}
                                error={errors[field.field] && touched[field.field]}
                                onChange={(e) => {
                                  handleFieldChange(e, field, setFieldValue);
                                }}
                                required={isDisabled ? false : field.isRequired}
                                disabled={
                                  credentialDialogMode === "view"
                                    ? true
                                    : field.type === "password" && isTokenVisible
                                    ? false
                                    : isDisabled
                                }
                                className={field.type === "password" && "input-with-icon"}
                                icon={{
                                  component:
                                    field.type === "password" &&
                                    isTokenVisible &&
                                    canViewToken
                                      ? "visibility"
                                      : field.type === "password" &&
                                        isTokenVisible === false &&
                                        canViewToken
                                      ? "visibility_off"
                                      : false,
                                  direction: "right",
                                  iconProps: {
                                    onClick: () => setIsTokenVisible((prev) => !prev),
                                    sx: { cursor: "pointer" }
                                  }
                                }}
                              />
                            );
                          })}
                    </>
                  )}
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
        {(credentialDialogMode === "edit" || credentialDialogMode === "view") && selectedCredential?.id && canDelete && (
          <SoftBox sx={{ px: { xs: 3, sm: 6 }, pb: 2 }}>
            <DeletePropertiesSection
              open={isCredentialDialogOpen}
              resourceName="credential"
              onDelete={() => {
                dispatch(closeCredentialDialog());
                dispatch(setDeleteCredentialDialogOpen(true));
              }}
            />
          </SoftBox>
        )}
        <DialogActions>
          <SoftButton
            onClick={handleDialogClose}
            variant="outlined"
            color="info"
            className={credentialDialogMode === "add" ? "btn-cancel-add-credentials" : ""}
          >
            {credentialDialogMode === "add" ? "Cancel" : "Close"}
          </SoftButton>
          {credentialDialogMode !== "view" && (
            <SoftButton variant="contained" color="info" onClick={handleAccept}>
              {isFormSubmitting ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#fff",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                    zIndex: 1
                  }}
                />
              ) : credentialDialogMode === "add" ? (
                "Add"
              ) : (
                "Save changes"
              )}
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
      <DiscardDialog
        open={discardDialogOpen}
        handleDialogClose={handleDiscardDialogClose}
        onAccept={handleDiscardChanges}
        acceptButtonColor="error"
      />
    </>
  );
}
